import React, { useState, useMemo } from 'react';
import { merge, noop } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import { CollectionsProvider } from '@fiverr-private/collect_actions';
import { ComplianceProvider } from '@fiverr-private/compliance';
import { PushkinConfiguration, useCurrentPageName } from '@fiverr-private/seller_pages_toolbox';
import { EngagementBadgeInfo } from '@fiverr-private/loyalty_utils';
import { NotableClientData } from '../../services/getNotableClients';
import { GigsData, Seller, TrustedFreelancer, HighlightsData, LocalizationData } from '../../types';
import { ReviewsData } from '../FreelancerPage/FreelancerReviews';
import { useIsUserOnlineQuery } from '../../hooks/useIsUserOnlineQuery';
import { useSellerDataQuery } from '../../hooks/useSellerDataQuery';
import { useLocalizationDataQuery } from '../../hooks/useLocalizationDataQuery';
import { useNotableClientsQuery } from '../../hooks/useNotableClientsQuery';
import { Consultation } from '../../types/gigs-kimera';
import { PAGE_NAME } from '../../monitoring';
import { CurrencyConfig } from '../../types/localization-kimera';
import { useRolloutContext } from '../../contexts/RolloutContext';

export interface FreelancerPageContextProps {
    username: string;
    isActivationCompleted: boolean | undefined;
    currency?: CurrencyConfig;
    reviewsData: ReviewsData | undefined;
    customOrdersAllowed: boolean;
    sellerPageData: Seller;
    gigsData: GigsData | undefined;
    consultationData: Consultation;
    trustedFreelancerData: TrustedFreelancer;
    isEligibleSeller: boolean;
    notableClients: NotableClientData[];
    highlightsData: HighlightsData;
    inVettedBadgeTest: boolean;
    localizationData: LocalizationData | null;
    pushkinConfiguration?: PushkinConfiguration;
    bannerData?: {
        proBenefitsBanner?: {
            wasClosed: boolean;
        };
    };
    isExperiential: boolean;
    loyaltyData: EngagementBadgeInfo | null;
    complianceData?: { state: any };
}

interface FreelancerPageContextData
    extends Omit<
        FreelancerPageContextProps,
        'seller' | 'gigsData' | 'sellerPageData' | 'localizationData' | 'bannerData'
    > {
    isLoggedIn: boolean;
    isOnline: boolean;
    isShowLearnModal: boolean;
    sellerId: string | number | undefined;
    seller: Seller | null;
    gigs: GigsData['gigs'];
    isCurrentUser: boolean;
    hasReviews: boolean;
    hasTestimonials: boolean;
    notableClients: NotableClientData[];
    setIsShowLearnModal(isShow: boolean): void;
    setIsTrustedFreelancer(isTrusted: boolean): void;
    sellerTimeZone: string | null;
    bannerData: {
        proBenefitsBanner: {
            wasClosed: boolean;
        };
    };
    isExperiential: boolean;
    loyaltyData: EngagementBadgeInfo | null;
    compliaceData?: { state?: any }; // todo fix export in compliace package
}

const initialContext: FreelancerPageContextData = {
    username: '',
    isActivationCompleted: true,
    customOrdersAllowed: false,
    isLoggedIn: false,
    isShowLearnModal: false,
    isOnline: false,
    sellerId: '',
    seller: {} as Seller,
    consultationData: {},
    trustedFreelancerData: {
        withSaveButton: false,
        isTrustedFreelancer: false,
    },
    highlightsData: {
        collaborateData: {},
        matchingIndustriesData: {},
        saveData: { freelancers: {} },
    },
    notableClients: [],
    gigs: [],
    reviewsData: undefined,
    hasReviews: false,
    hasTestimonials: false,
    isCurrentUser: true,
    isEligibleSeller: false,
    setIsShowLearnModal: noop,
    setIsTrustedFreelancer: noop,
    inVettedBadgeTest: false,
    sellerTimeZone: null,
    bannerData: {
        proBenefitsBanner: {
            wasClosed: false,
        },
    },
    isExperiential: false,
    loyaltyData: null,
};

export const FreelancerPageContext = React.createContext<FreelancerPageContextData>(initialContext);

export const FreelancerPageContextProvider: React.FC<React.PropsWithChildren<FreelancerPageContextProps>> = ({
    username,
    isActivationCompleted,
    sellerPageData,
    reviewsData,
    gigsData,
    currency,
    consultationData,
    trustedFreelancerData,
    highlightsData,
    customOrdersAllowed,
    isEligibleSeller,
    notableClients,
    inVettedBadgeTest,
    children,
    localizationData,
    pushkinConfiguration,
    bannerData,
    isExperiential,
    loyaltyData,
    complianceData,
}) => {
    const { userId, organizationId } = getContext();

    const [isShowLearnModal, setIsShowLearnModal] = useState(false);

    const { inStorksBackgroundCheckRollout } = useRolloutContext();

    const [isTrustedFreelancer, setIsTrustedFreelancer] = useState(trustedFreelancerData?.isTrustedFreelancer);

    const { data: { isOnline = false } = {} } = useIsUserOnlineQuery(username);

    const { data: seller = null } = useSellerDataQuery({
        username,
        initialData: sellerPageData,
    });

    const hasTestimonials = (seller?.testimonials?.length || 0) > 0;

    const sellerId = seller ? seller.user.id : '';
    const { data: clients = [] } = useNotableClientsQuery({
        sellerId,
        initialData: notableClients,
    });

    const { data: localization = null } = useLocalizationDataQuery({
        sellerId,
        initialData: localizationData,
    });

    const isCurrentUser = userId?.toString() === sellerId?.toString();
    const hasReviews = !!reviewsData && reviewsData?.total_count > 0;

    const value = useMemo(
        () => ({
            isLoggedIn: !!userId,
            isShowLearnModal,
            username: seller?.user?.name || username,
            isActivationCompleted,
            sellerId,
            isOnline,
            currency,
            seller,
            highlightsData: highlightsData || {},
            gigs: gigsData?.gigs || [],
            consultationData,
            reviewsData,
            trustedFreelancerData: {
                ...trustedFreelancerData,
                isTrustedFreelancer,
            },
            isCurrentUser,
            customOrdersAllowed,
            isEligibleSeller,
            hasReviews,
            hasTestimonials,
            notableClients: clients,
            setIsShowLearnModal,
            setIsTrustedFreelancer,
            inVettedBadgeTest,
            sellerTimeZone: localization?.timezone,
            pushkinConfiguration,
            bannerData: merge(initialContext.bannerData, bannerData),
            isExperiential,
            loyaltyData,
        }),
        [
            trustedFreelancerData,
            highlightsData,
            userId,
            sellerId,
            username,
            isActivationCompleted,
            clients,
            currency,
            gigsData,
            hasReviews,
            hasTestimonials,
            customOrdersAllowed,
            isEligibleSeller,
            isOnline,
            isShowLearnModal,
            isTrustedFreelancer,
            isCurrentUser,
            reviewsData,
            seller,
            inVettedBadgeTest,
            localization,
            consultationData,
            pushkinConfiguration,
            bannerData,
            isExperiential,
            loyaltyData,
        ]
    );

    const pageName = useCurrentPageName();

    const selfView = userId === Number(sellerId);

    const Wrapper =
        inStorksBackgroundCheckRollout && !selfView ? (
            <ComplianceProvider
                complianceData={complianceData?.state}
                requester_id={userId}
                organizationId={organizationId}
                pageName={pageName}
            >
                <FreelancerPageContext.Provider value={value}>{children}</FreelancerPageContext.Provider>
            </ComplianceProvider>
        ) : (
            <FreelancerPageContext.Provider value={value}>{children}</FreelancerPageContext.Provider>
        );

    return <CollectionsProvider bi={{ source: PAGE_NAME }}>{Wrapper}</CollectionsProvider>;
};
